<template>
  <div>
    <slot
      :about-open="aboutOpen"
      :clear-session="clearSession"
      :currently-open-side-nav-menu="currentlyOpenSideNavMenu"
      :focus-global-search="focusGlobalSearch"
      :for-pet-parents-open="forPetParentsOpen"
      :for-vets-open="forVetsOpen"
      :get-user="getUser"
      :has-user="hasUser"
      :is-client="isClient"
      :is-global-search-focused="isGlobalSearchFocused"
      :is-guest="isGuest"
      :is-onboarding="isOnboarding"
      :is-partner="isPartner"
      :mobile-open="mobileOpen"
      :mobile-region-open="mobileRegionOpen"
      :open-auth-modal="openAuthModal"
      :photo-url="photoUrl"
      :prefixed-app-url="prefixedAppUrl"
      :profile-open="profileOpen"
      :region-open="regionOpen"
      :select-locale-option="selectLocaleOption"
      :set-house-ad-ref="setHouseAdRef"
      :set-ref="setRef"
      :side-nav-about-open="sideNavAboutOpen"
      :side-nav-for-pets-open="sideNavForPetsOpen"
      :side-nav-for-vets-open="sideNavForVetsOpen"
      :sign-out="signOut"
      :toggle-about-menu="toggleAboutMenu"
      :toggle-for-pet-parents-menu="toggleForPetParentsMenu"
      :toggle-for-vets-menu="toggleForVetsMenu"
      :toggle-mobile-menu="toggleMobileMenu"
      :toggle-mobile-region-menu="toggleMobileRegionMenu"
      :toggle-profile-menu="toggleProfileMenu"
      :toggle-region-menu="toggleRegionMenu"
      :toggle-side-nav-menu-option="toggleSideNavMenuOption"
    />
  </div>
</template>

<script>
import trimStart from 'lodash/trimStart';
import { mapActions, mapGetters } from 'vuex';

import useBrowserApiHelpers from '@/composables/use-browser-api-helpers';
import useCookies from '@/composables/use-cookies';
import SocialiteProvider from '@/enums/SocialiteProvider';
import { getAppUrl } from '@/helpers/url';
import ClickAway from '@/mixins/ClickAway';
import InteractsWithGlobalSearch from '@/mixins/InteractsWithGlobalSearch';
import InteractsWithHubSpot from '@/mixins/InteractsWithHubSpot';
import InteractsWithScroll from '@/mixins/InteractsWithScroll';
import ManagesClientSession from '@/mixins/ManagesClientSession';
import ManagesLocaleCookies from '@/mixins/ManagesLocaleCookies';
import VetsterNavHouseAd from '@/mixins/VetsterNavHouseAd';
import googleAnalyticsTrack from '@/packages/googleAnalyticsTrack';

export default {
  name: 'VetsterNav',

  mixins: [
    ClickAway,
    InteractsWithGlobalSearch,
    InteractsWithHubSpot,
    InteractsWithScroll,
    ManagesClientSession,
    ManagesLocaleCookies,
    VetsterNavHouseAd,
  ],

  props: {
    localLinks: {
      type: Object,
      required: true,
    },
  },

  setup() {
    const { localStorageAvailable } = useBrowserApiHelpers();
    const { removeVetsterCookies } = useCookies();

    return {
      localStorageAvailable,
      removeVetsterCookies,
    };
  },

  data() {
    return {
      aboutOpen: false,
      currentlyOpenSideNavMenu: null,
      forPetParentsOpen: false,
      forVetsOpen: false,
      mobileOpen: false,
      mobileRegionOpen: false,
      profileOpen: false,
      regionOpen: false,
      sideNavAboutOpen: false,
      sideNavForPetsOpen: false,
      sideNavForVetsOpen: false,
      slotRefs: {
        aboutMenu: null,
        aboutMenuButton: null,
        forPetParentsMenu: null,
        forPetParentsMenuButton: null,
        forVetsMenu: null,
        forVetsMenuButton: null,
        mobileMenu: null,
        mobileMenuButton: null,
        profileMenu: null,
        profileMenuButton: null,
        regionMenu: null,
        regionMenuButton: null,
      },
    };
  },

  computed: {
    ...mapGetters([
      'getUser',
      'hasUser',
      'isClient',
      'isOnboarding',
      'isPartner',
    ]),
    isGuest() {
      return !this.hasUser;
    },
    photoUrl() {
      return this.getUser?.photo?.uri_tiny;
    },
  },

  watch: {
    aboutOpen(isOpen) {
      if (!isOpen) {
        this.slotRefs.aboutMenu = null;
      }
    },
    forPetParentsOpen(isOpen) {
      if (!isOpen) {
        this.slotRefs.forPetParentsMenu = null;
      }
    },
    forVetsMenu(isOpen) {
      if (!isOpen) {
        this.slotRefs.forVetsMenu = null;
      }
    },
    mobileOpen(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.$bus.$emit('disable-scroll');
          this.hideHubSpotIcon();
        });
      } else {
        this.slotRefs.mobileMenu = null;
        this.$bus.$emit('enable-scroll');
        this.showHubSpotIcon();
      }
    },
    profileOpen(isOpen) {
      if (!isOpen) {
        this.slotRefs.profileMenu = null;
      }
    },
    regionOpen(isOpen) {
      if (!isOpen) {
        this.slotRefs.regionMenu = null;
      }
    },
  },

  created() {
    this.$store.commit('setLocalLinks', this.localLinks);
    this.$bus.$on('escape', this.handleEscape);
    this.$bus.$on('disable-scroll', this.disableScroll);
    this.$bus.$on('enable-scroll', this.enableScroll);
    this.checkAuthAndUser(true);
  },

  mounted() {
    window.addEventListener('resize', this.handleWindowResize);
    this.$layout.calculateNavHeight();
    this.$nextTick(() => {
      document.addEventListener('click', this.registerClickAway);
    });
  },

  beforeDestroy() {
    this.$bus.$off('escape', this.handleEscape);
    this.$bus.$off('disable-scroll', this.disableScroll);
    this.$bus.$off('enable-scroll', this.enableScroll);
    window.removeEventListener('resize', this.handleWindowResize);
  },

  methods: {
    ...mapActions(['checkAuthAndUser']),

    ...mapActions('authModal', ['setAuthModalMode']),

    closeAboutMenu() {
      this.aboutOpen = false;
    },

    closeForPetParentsMenu() {
      this.forPetParentsOpen = false;
    },

    closeForVetAndTechsMenu() {
      this.forVetsOpen = false;
    },

    closeMobileMenu() {
      this.mobileOpen = false;
    },

    closeMobileRegionMenu() {
      this.mobileRegionOpen = false;
    },

    closeProfileMenu() {
      this.profileOpen = false;
    },

    closeRegionMenu() {
      this.regionOpen = false;
    },

    handleEscape() {
      this.closeProfileMenu();
      this.closeRegionMenu();
      this.closeMobileRegionMenu();
      this.closeMobileMenu();
      this.closeForPetParentsMenu();
      this.closeForVetAndTechsMenu();
      this.closeAboutMenu();
    },

    handleWindowResize() {
      // Check if the menu is open and the screen size is greater than lg
      if (this.mobileOpen && this.$screen('lg')) {
        // Close the menu and reset the style
        this.mobileOpen = false;
      }
    },

    openAuthModal(mode) {
      this.setAuthModalMode(mode);
      this.profileOpen = false;
    },

    prefixedAppUrl(path) {
      if (!this.hasUser) {
        return getAppUrl('/');
      }

      const segment = this.isPartner ? 'px' : 'cx';

      if (this.isOnboarding) {
        return getAppUrl(`${segment}/welcome`);
      }

      return getAppUrl(`${segment}/${trimStart(path, '/')}`);
    },

    registerClickAway(event) {
      this.hideOnClickAway(
        event,
        this.slotRefs.profileMenu,
        this.closeProfileMenu,
        this.slotRefs.profileMenuButton,
      );
      this.hideOnClickAway(
        event,
        this.slotRefs.regionMenu,
        this.closeRegionMenu,
        this.slotRefs.regionMenuButton,
      );
      this.hideOnClickAway(
        event,
        this.slotRefs.mobileMenu,
        this.closeMobileMenu,
        this.slotRefs.mobileMenuButton,
      );
      this.hideOnClickAway(
        event,
        this.slotRefs.forPetParentsMenu,
        this.closeForPetParentsMenu,
        this.slotRefs.forPetParentsMenuButton,
      );
      this.hideOnClickAway(
        event,
        this.slotRefs.forVetsMenu,
        this.closeForVetAndTechsMenu,
        this.slotRefs.forVetsMenuButton,
      );
      this.hideOnClickAway(
        event,
        this.slotRefs.aboutMenu,
        this.closeAboutMenu,
        this.slotRefs.aboutMenuButton,
      );
    },

    setRef(name, el) {
      this.slotRefs[name] = el;
    },

    async signOut() {
      this.$toast(this.$t('login.signed-out-see-you-soon'));

      if (this.isGuest) {
        return true;
      }

      SocialiteProvider.values().forEach((provider) => {
        this.$store.dispatch('socialite/providerSdkLogout', {
          component: this,
          provider_type: provider.value,
        });
      });

      await this.$auth.signOut();

      this.$sentry.setUser(null);

      try {
        if (this.localStorageAvailable()) {
          window.localStorage.removeItem('userId');
        }
      } catch {
        //
      }

      this.removeVetsterCookies();

      this.clearSession();

      googleAnalyticsTrack(() => {
        this.reset();
      });
      this.$analytics.track({
        eventName: 'user_sign_out',
        parent: 'auth',
        child: 'sign-out',
      });
      window.location.href = '/';

      return true;
    },

    toggleAboutMenu() {
      this.aboutOpen = !this.aboutOpen;
    },

    toggleForPetParentsMenu() {
      this.forPetParentsOpen = !this.forPetParentsOpen;
    },

    toggleForVetsMenu() {
      this.forVetsOpen = !this.forVetsOpen;
    },

    toggleMobileMenu() {
      this.mobileOpen = !this.mobileOpen;
      if (this.mobileOpen) {
        this.mobileRegionOpen = false;
      }
    },

    toggleMobileRegionMenu() {
      this.mobileRegionOpen = !this.mobileRegionOpen;
    },

    toggleProfileMenu() {
      this.profileOpen = !this.profileOpen;
      this.regionOpen = false;
    },

    toggleRegionMenu() {
      this.regionOpen = !this.regionOpen;
      this.profileOpen = false;
    },

    toggleSideNavMenuOption(menuName) {
      // Toggle the clicked menu
      this[menuName] = !this[menuName];

      // Close other open menu if there is one
      if (this.currentlyOpenSideNavMenu && this.currentlyOpenSideNavMenu !== menuName) {
        this[this.currentlyOpenSideNavMenu] = false;
      }

      // Update the currentlyOpenMenu variable
      this.currentlyOpenSideNavMenu = this[menuName] ? menuName : null;
    },
  },
};
</script>
